import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
    LayoutDashboard, 
    Mountain, 
    Calendar, 
    LogOut,
    Eye,
    ChevronRight,
    Sun,
    Moon,
    UserCheck,
    Shield,
    Layout,
    MessageSquare,
    BookOpen,
    ExternalLink,
    Menu,
    X
} from 'lucide-react';

import { Expedition } from '../../types';
import { BUILD_INFO } from './Dashboard';

interface AdminLayoutProps {
    expeditions: Expedition[];
    setExpeditions: React.Dispatch<React.SetStateAction<Expedition[]>>;
}

interface NavItem {
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    sublabel?: string;
    badge?: number | string;
    badgeColor?: string;
    isExternal?: boolean;
    tag?: string;
}

interface NavSection {
    id: string;
    title: string;
    items: NavItem[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ expeditions, setExpeditions }) => {
    const location = useLocation();
    const [theme, setTheme] = React.useState<'light' | 'dark'>('light');
    const [confirmedBookings, setConfirmedBookings] = useState<any[]>([]);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    useEffect(() => {
        supabase
            .from('bookings')
            .select('id, payment_status, created_at')
            .in('payment_status', ['paid', 'confirmed'])
            .order('created_at', { ascending: false })
            .then(({ data }) => setConfirmedBookings(data || []));

        supabase.auth.getUser().then(({ data: { user } }) => {
            setUserEmail(user?.email ?? null);
        });
    }, []);

    // Cerrar menú móvil al navegar
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    // Estructura agrupada estilo WordPress / Fadech
    const navSections: NavSection[] = [
        {
            id: 'principal',
            title: 'Principal',
            items: [
                { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
                { path: '/admin/web', icon: Layout, label: 'Editar Web' },
            ]
        },
        {
            id: 'operaciones',
            title: 'Operaciones & CRM',
            items: [
                { 
                    path: '/admin/bookings', 
                    icon: UserCheck, 
                    label: 'Reservas (CRM)', 
                    badge: confirmedBookings.length > 0 ? confirmedBookings.length : undefined,
                    badgeColor: 'green'
                },
                { path: '/admin/calendar', icon: Calendar, label: 'Calendario y Fechas' },
                { path: '/admin/expeditions', icon: Mountain, label: 'Expediciones' },
            ]
        },
        {
            id: 'marketing',
            title: 'Marketing & Contenidos',
            items: [
                { 
                    path: 'https://fisuraexpeditions.sanity.studio', 
                    icon: BookOpen, 
                    label: 'Blog Studio', 
                    isExternal: true,
                    tag: 'Sanity'
                },
                { path: '/admin/reviews', icon: MessageSquare, label: 'Reseñas' },
            ]
        },
        {
            id: 'sistema',
            title: 'Configuración',
            items: [
                { path: '/admin/users', icon: Shield, label: 'Accesos y Roles' },
            ]
        }
    ];

    const isActive = (path: string) => {
        if (path === '/admin') return location.pathname === '/admin';
        return location.pathname.startsWith(path);
    };

    const renderSidebarContent = (isMobile = false) => (
        <div className="flex flex-col h-full">
            {/* Header del Sidebar con Logo Grande y Centrado */}
            <div className={`py-5 px-4 border-b flex flex-col items-center justify-center relative flex-shrink-0 ${
                theme === 'dark' ? 'border-rock/70 bg-black/20' : 'border-gray-100 bg-gray-50/50'
            }`}>
                {isMobile && (
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className={`absolute right-3 top-3 p-1.5 rounded-md transition-colors ${
                            theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                        aria-label="Cerrar menú"
                    >
                        <X className="w-5 h-5" />
                    </button>
                )}

                <Link 
                    to="/admin" 
                    onClick={() => isMobile && setMobileMenuOpen(false)}
                    className="flex flex-col items-center justify-center group"
                >
                    <img 
                        src={theme === 'dark' ? "/logo-fisura-white.png" : "/logo-fisura-black.png"} 
                        className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105" 
                        alt="Fisura Expeditions" 
                    />
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-[9px] text-[#00d4ff] font-black tracking-[0.3em] uppercase select-none">
                            Backoffice
                        </span>
                        <span className="text-[8px] font-bold bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/30 px-1.5 py-0.5 rounded tracking-wider font-mono">
                            {BUILD_INFO.version}
                        </span>
                    </div>
                </Link>
            </div>

            {/* Barra de Usuario Compacta */}
            <div className={`px-3 py-2 border-b flex items-center justify-between gap-2 flex-shrink-0 ${
                theme === 'dark' ? 'border-rock/50 bg-black/10' : 'border-gray-100 bg-white'
            }`}>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-md bg-[#00d4ff]/15 border border-[#00d4ff]/40 flex items-center justify-center font-bold text-[11px] text-[#00d4ff] flex-shrink-0">
                        {userEmail ? userEmail.charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span className={`text-[11px] font-bold truncate leading-tight ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                            Admin Fisura
                        </span>
                        <span className="text-[9px] text-[#00d4ff] truncate leading-tight font-medium" title={userEmail || 'Super Admin'}>
                            {userEmail || 'Super Admin'}
                        </span>
                    </div>
                </div>

                <button
                    onClick={toggleTheme}
                    className={`p-1.5 rounded-md transition-colors ${
                        theme === 'dark' 
                        ? 'text-yellow-400 hover:bg-white/10' 
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={theme === 'dark' ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'}
                >
                    {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                </button>
            </div>

            {/* Navegación Scrollable */}
            <div className="flex-1 overflow-y-auto px-2.5 py-3 space-y-3 custom-admin-scroll">
                {navSections.map((section) => (
                    <div key={section.id} className="space-y-0.5">
                        <p className="text-[9px] font-extrabold uppercase tracking-[0.18em] px-2.5 pb-1 text-gray-400 dark:text-stone-500">
                            {section.title}
                        </p>

                        {section.items.map((item) => {
                            const ItemIcon = item.icon;

                            if (item.isExternal) {
                                return (
                                    <a
                                        key={item.label}
                                        href={item.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => isMobile && setMobileMenuOpen(false)}
                                        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 group border border-transparent ${
                                            theme === 'dark'
                                            ? 'text-gray-300 hover:text-white hover:bg-white/5 hover:border-white/10'
                                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 hover:border-gray-200'
                                        }`}
                                        title="Abrir Sanity Studio en una nueva pestaña"
                                    >
                                        <div className="w-4 h-4 rounded flex items-center justify-center text-[#F03E2F] flex-shrink-0">
                                            <ItemIcon className="w-4 h-4" />
                                        </div>
                                        <span className="truncate">{item.label}</span>
                                        {item.tag && (
                                            <span className="ml-auto text-[8px] font-bold px-1.5 py-0.2 rounded bg-[#F03E2F]/15 text-[#F03E2F] border border-[#F03E2F]/30 uppercase tracking-wider">
                                                {item.tag}
                                            </span>
                                        )}
                                        <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-[#F03E2F] transition-colors flex-shrink-0 ml-1" />
                                    </a>
                                );
                            }

                            const active = isActive(item.path);

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => isMobile && setMobileMenuOpen(false)}
                                    className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px] transition-all duration-150 group ${
                                        active 
                                        ? theme === 'dark'
                                            ? 'bg-[#00d4ff]/15 text-[#00d4ff] font-bold border-l-2 border-[#00d4ff]' 
                                            : 'bg-[#00d4ff]/10 text-cyan-800 font-bold border-l-2 border-[#00d4ff]'
                                        : theme === 'dark' 
                                            ? 'text-gray-400 hover:text-white hover:bg-white/5 font-medium' 
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium'
                                    }`}
                                >
                                    <ItemIcon className={`w-4 h-4 flex-shrink-0 ${
                                        active ? 'text-[#00d4ff]' : 'text-gray-400 group-hover:text-current'
                                    }`} />
                                    <span className="truncate">{item.label}</span>
                                    
                                    {item.badge !== undefined && (
                                        <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                                            theme === 'dark' ? 'bg-[#25D366]/20 text-[#25D366]' : 'bg-green-100 text-green-700'
                                        }`}>
                                            {item.badge}
                                        </span>
                                    )}

                                    {active && !item.badge && (
                                        <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Footer Compacto */}
            <div className={`px-3 py-2.5 border-t flex items-center justify-between gap-1 flex-shrink-0 ${
                theme === 'dark' ? 'border-rock/70 bg-black/20' : 'border-gray-100 bg-gray-50/70'
            }`}>
                <Link 
                    to="/" 
                    target="_blank"
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                        theme === 'dark' 
                        ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/70'
                    }`}
                    title="Ver sitio web público"
                >
                    <Eye className="w-3.5 h-3.5 text-[#00d4ff]" />
                    <span>Ver Web</span>
                </Link>

                <button 
                    onClick={async () => {
                        await supabase.auth.signOut();
                    }}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                        theme === 'dark'
                        ? 'text-red-400/80 hover:text-red-400 hover:bg-red-500/10'
                        : 'text-red-600/80 hover:text-red-600 hover:bg-red-50'
                    }`}
                    title="Cerrar sesión"
                >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Salir</span>
                </button>
            </div>
        </div>
    );

    return (
        <div className={`flex flex-col md:flex-row min-h-screen font-sans transition-colors duration-500 ${
            theme === 'dark' ? 'bg-[#0a0a0a] text-[#f2f2f2]' : 'bg-[#f8fafc] text-gray-900'
        }`}>
            {/* Topbar Superior para Móviles (md:hidden) */}
            <header className={`md:hidden sticky top-0 z-30 px-4 py-3 border-b flex items-center justify-between transition-colors ${
                theme === 'dark' ? 'bg-[#15110e] border-rock' : 'bg-white border-gray-200 shadow-sm'
            }`}>
                <button
                    onClick={() => setMobileMenuOpen(true)}
                    className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark' ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="Abrir menú"
                >
                    <Menu className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2">
                    <img 
                        src={theme === 'dark' ? "/logo-fisura-white.png" : "/logo-fisura-black.png"} 
                        className="h-7 w-auto object-contain" 
                        alt="Fisura Expeditions" 
                    />
                    <span className="text-[8px] font-mono font-bold bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/30 px-1.5 py-0.5 rounded">
                        {BUILD_INFO.version}
                    </span>
                </div>

                <button
                    onClick={toggleTheme}
                    className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark' ? 'text-yellow-400 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-label="Cambiar tema"
                >
                    {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
            </header>

            {/* Backdrop / Oscurecedor para Móviles */}
            {mobileMenuOpen && (
                <div 
                    onClick={() => setMobileMenuOpen(false)}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity animate-in fade-in"
                    aria-hidden="true"
                />
            )}

            {/* Sidebar Desktop (fijo / sticky en pantallas medianas y grandes) */}
            <aside className={`hidden md:flex md:w-64 border-r flex-col sticky top-0 h-screen z-30 transition-colors duration-500 flex-shrink-0 select-none ${
                theme === 'dark' ? 'bg-[#15110e] border-rock' : 'bg-white border-gray-200 shadow-lg'
            }`}>
                {renderSidebarContent(false)}
            </aside>

            {/* Sidebar Móvil (Drawer Deslizable) */}
            <aside className={`fixed inset-y-0 left-0 w-72 max-w-[85vw] z-50 transform transition-transform duration-300 ease-in-out md:hidden flex flex-col shadow-2xl select-none ${
                mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } ${theme === 'dark' ? 'bg-[#15110e] border-r border-rock' : 'bg-white border-r border-gray-200'}`}>
                {renderSidebarContent(true)}
            </aside>

            {/* Contenido Principal (Ahora ocupa el 100% en teléfonos) */}
            <main className="flex-1 flex flex-col relative overflow-y-auto min-h-[calc(100vh-57px)] md:min-h-screen">
                <div className="p-4 sm:p-6 md:p-8 lg:p-10 flex-1">
                    <Outlet context={{ theme, expeditions, setExpeditions }} />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
